import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, MessageCircle, Mail, ArrowUpDown } from 'lucide-react';
import { customersApi } from '../../api/endpoints/customers';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { SearchInput } from '../../components/ui/SearchInput';
import { FilterDropdown } from '../../components/ui/FilterDropdown';
import { Input } from '../../components/ui/Input';
import { DateFilterBar } from '../../components/ui/DateFilterBar';
import { useToast } from '../../components/ui/Alert';
import { useDebounce } from '../../hooks/useDebounce';
import { usePagination } from '../../hooks/usePagination';
import { formatDate } from '../../lib/formatters';
import type { Customer } from '../../types/customer';

export function CustomerList() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { page, limit, goToPage } = usePagination({
    resetDeps: [debouncedSearch, sortDir, startDate, endDate],
  });

  // Export modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['customers', storeId, { page, limit, search: debouncedSearch, sort: sortDir, startDate, endDate }],
    queryFn: () => customersApi.list(storeId!, {
      page, limit,
      search: debouncedSearch || undefined,
      sort: sortDir !== 'desc' ? sortDir : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    enabled: !!storeId,
  });

  const handleExport = async () => {
    try {
      const result = await customersApi.exportCsv(storeId!, {
        startDate: exportStartDate || undefined,
        endDate: exportEndDate || undefined,
      });
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers-${exportStartDate || 'all'}-${exportEndDate || 'now'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast('success', t('common.success'));
      setShowExportModal(false);
    } catch {
      toast('error', t('common.error'));
    }
  };

  const openExportModal = () => {
    setExportStartDate('');
    setExportEndDate('');
    setShowExportModal(true);
  };

  const columns: Column<Customer>[] = [
    {
      key: 'name',
      header: t('common.name'),
      sortable: true,
      render: (c) => {
        const displayName = c.realName || c.name || c.email || '-';
        return (
          <span
            className="font-medium text-accent cursor-pointer hover:underline"
            onClick={() => navigate(`/${storeId}/customers/${c._id}`)}
          >
            {displayName}
          </span>
        );
      },
    },
    {
      key: 'email',
      header: t('common.email'),
      render: (c) => <span className="text-text-secondary">{c.email ?? '-'}</span>,
    },
    {
      key: 'phone',
      header: t('common.phone'),
      render: (c) => (
        c.phoneNumber ? (
          <span className="inline-block text-text-secondary tabular-nums" dir="ltr">
            {c.phoneCountryCode ? `+${c.phoneCountryCode} ${c.phoneNumber}` : String(c.phoneNumber)}
          </span>
        ) : <span className="text-text-secondary">-</span>
      ),
    },
    {
      key: 'orders',
      header: t('customers.orders'),
      render: (c) => <span>{c.ordersCount ?? 0}</span>,
    },
    {
      key: 'date',
      header: t('common.date'),
      sortable: true,
      render: (c) => <span className="text-xs text-text-secondary">{c.createdAt ? formatDate(c.createdAt) : '-'}</span>,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      render: (c) => (
        <div className="flex items-center gap-1">
          {c.phoneNumber && (() => {
            const waDigits = `${c.phoneCountryCode ?? ''}${c.phoneNumber}`.replace(/\D/g, '');
            return waDigits ? (
              <a
                href={`https://wa.me/${waDigits}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-white bg-[#25D366] hover:bg-[#1fb855] transition-colors"
                title={t('customers.whatsapp', 'WhatsApp')}
              >
                <MessageCircle className="h-3.5 w-3.5" />
              </a>
            ) : null;
          })()}
          {c.email && (
            <a
              href={`mailto:${c.email}`}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-text-muted hover:bg-bg-hover hover:text-accent transition-colors"
              title={t('customers.email', 'Email')}
            >
              <Mail className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      ),
    },
  ];

  const customers = data?.customers ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.pages ?? 1;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold">{t('sidebar.customers')}</h1>
            {total != null && (
              <span className="rounded-full bg-bg-tertiary px-2.5 py-0.5 text-xs font-medium text-text-secondary">
                {total}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-text-secondary">{t('customers.description', 'Customer information and management')}</p>
        </div>
        <Button variant="secondary" size="sm" onClick={openExportModal}>
          <Download className="h-4 w-4" />
          {t('customers.exportCsv')}
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('common.search') + '...'}
          className="w-full sm:w-64"
        />
        <FilterDropdown
          icon={ArrowUpDown}
          options={[
            { value: 'desc', label: t('common.newestFirst', 'Newest first') },
            { value: 'asc', label: t('common.oldestFirst', 'Oldest first') },
          ]}
          value={sortDir}
          onChange={(v) => setSortDir(v as 'desc' | 'asc')}
        />
      </div>
      <DateFilterBar
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        className="mb-4"
      />

      <div className="rounded-xl border border-border-default bg-bg-secondary shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={customers}
          isLoading={isLoading}
          keyExtractor={(c) => c._id}
          emptyTitle={t('common.noResults')}
          emptyDescription={t('common.tryDifferentSearch')}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={goToPage}
        />
      </div>

      {/* Export Modal */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title={t('customers.exportCsv')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowExportModal(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleExport}>
              <Download className="h-4 w-4" />
              {t('customers.exportCsv')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label={t('common.startDate', 'Start Date')}
              type="date"
              value={exportStartDate}
              onChange={(e) => setExportStartDate(e.target.value)}
            />
            <Input
              label={t('common.endDate', 'End Date')}
              type="date"
              value={exportEndDate}
              onChange={(e) => setExportEndDate(e.target.value)}
            />
          </div>
          <p className="text-xs text-text-muted">{t('customers.exportHint', 'Leave dates empty to export all customers.')}</p>
        </div>
      </Modal>
    </div>
  );
}
